import { useEffect, useRef } from "react";
const NOT_DISABLED = ':not(:disabled):not([aria-hidden="true"]):not([hidden])';
const FOCUSABLE_SELECTOR = [
    `input${NOT_DISABLED}`,
    `select${NOT_DISABLED}`,
    `textarea${NOT_DISABLED}`,
    `[tabindex]${NOT_DISABLED}:not([tabindex="-1"])`
].join(", ");
function isVisible(el) {
    if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
    }
    // Fallback: offsetParent is null for display:none (except on fixed elements)
    return el.offsetParent !== null;
}
/**
 * Returns a ref to attach to a container (e.g. an Accordion).
 * When `shouldFocus` is true, auto-focuses after the next animation frame.
 *
 * `focusTarget`:
 * - `"input"`: focuses the first visible interactive element inside the region.
 * - `"region"` (default): focuses the region container itself (should have `tabIndex={-1}`).
 *    Better for screen readers as it announces the region context before the user tabs into fields.
 *
 * If `inputRegionClassName` is omitted, the container ref itself is used as the search scope.
 */
export function useAutoFocus(shouldFocus, inputRegionClassName, focusTarget = "region") {
    const containerRef = useRef(null);
    useEffect(() => {
        if (!shouldFocus || !containerRef.current)
            return;
        const frameId = requestAnimationFrame(() => {
            if (!containerRef.current)
                return;
            const searchRoot = inputRegionClassName
                ? containerRef.current.querySelector(`.${inputRegionClassName}`)
                : containerRef.current;
            if (!searchRoot)
                return;
            if (focusTarget === "region") {
                searchRoot.focus();
            }
            else {
                const candidates = searchRoot.querySelectorAll(FOCUSABLE_SELECTOR);
                for (const el of candidates) {
                    if (isVisible(el)) {
                        el.focus();
                        break;
                    }
                }
            }
        });
        return () => cancelAnimationFrame(frameId);
    }, [shouldFocus, inputRegionClassName, focusTarget]);
    return { ref: containerRef };
}
//# sourceMappingURL=useAutoFocus.js.map